import type { Component } from "vue";
import {
  BookOpen,
  Calendar,
  ClipboardList,
  Contact,
  File,
  FileText,
  Hash,
  ListTodo,
  Mail,
  MessageSquare,
  Package,
  Receipt,
  ShoppingCart,
  Truck,
  User,
  Users,
} from "@lucide/vue";
import {
  fold,
  isSuggestionEligible,
  words,
  type NxCommand,
  type NxCommandCategory,
  type NxCommandMatch,
  type NxCommandScope,
  type NxCommandSource,
  type NxCommandSuggester,
  type NxCommandSuggestContext,
  type NxCommandSuggestion,
} from "@blpde/nx-ui";
import {
  mergeSuggestions,
  type SearchDictionaryActions,
  type SuggestionAnswer,
  type SuggestionWire,
} from "@blpde/connect";
import { MASTER_MODULE, moduleApiRequest } from "@/moduleApi";
import { moduleRank, orderKinds, type SearchSettings } from "./settings";

/**
 * The universal search of the command palette (I-2348): every module the
 * account may use answers `GET /v1/search/hits` and `GET /v1/search/kinds` the
 * same way (backend/module/search). This file turns each module into one
 * palette source — its kinds become keyword scopes, its hits entries grouped
 * by kind — in the account's order. Modules that advertise `suggest` also
 * feed one spelling suggester (`GET /v1/search/suggest`).
 */

/** A kind as a module's catalog declares it. */
export interface SearchKind {
  key: string;
  label: Record<string, string>;
  keywords: Record<string, string[]>;
  icon?: string;
  listTarget?: string;
  fulltext?: boolean;
}

export interface SearchCatalog {
  module: string;
  kinds: SearchKind[];
  /** The module answers `GET /v1/search/suggest`. Absent or false: never ask. */
  suggest?: boolean;
}

export interface SearchHit {
  kind: string;
  id: string;
  number?: string;
  title: string;
  subtitle?: string;
  snippet?: string;
  target: string;
  match: NxCommandMatch;
  score?: number;
  date?: string;
}

export interface SearchAnswer {
  module: string;
  results: { kind: string; hits: SearchHit[]; more?: boolean }[];
  failed?: string[];
}

/** A module the palette may ask, with the label its group shows. */
export interface SearchModule {
  key: string;
  label: string;
}

export interface UniversalSearchContext {
  modules: SearchModule[];
  settings: SearchSettings;
  locale: string;
  t: (key: string, named?: Record<string, unknown>) => string;
  open: (target: string) => void;
  /** Reopens the palette with a query, e.g. a kind's keyword plus the text. */
  reopen?: (query: string) => void;
  /** Formats a hit's ISO date for the preview. */
  formatDate?: (iso: string) => string;
  /** Fetches a module's endpoint (test seam). */
  request?: (
    module: string,
    path: string,
    signal?: AbortSignal,
  ) => Promise<Response>;
  /**
   * Folds master hides or blocks. Read at suggest time. Absent: nothing is
   * hidden, so a suggestion is not held back while the filter fetch is still
   * out.
   */
  dictionaryFilters?: () => {
    hidden: ReadonlySet<string>;
    /** Folds of the resolved personal dictionary: a term in it gets no suggestion at all. */
    personal?: ReadonlySet<string>;
  };
  /**
   * Where a refused „nicht mehr vorschlagen“ shows (the shell's write-failure
   * toast). `failed` gets the cause; `resolved` runs when a later try works,
   * so the toast of the failure can leave. Absent: nothing is shown.
   */
  writeReporter?: {
    failed: (cause: unknown) => void;
    resolved?: () => void;
  };
}

export { MASTER_MODULE };

/** A module's search endpoint (`path` inside `/v1`), through the shell's one way to a module's API. */
export function moduleRequest(
  module: string,
  path: string,
  signal?: AbortSignal,
): Promise<Response> {
  return moduleApiRequest(module, `/v1${path}`, { signal });
}

const ICONS: Record<string, Component> = {
  "book-open": BookOpen,
  calendar: Calendar,
  "clipboard-list": ClipboardList,
  contact: Contact,
  file: File,
  "file-text": FileText,
  hash: Hash,
  "list-todo": ListTodo,
  mail: Mail,
  "message-square": MessageSquare,
  package: Package,
  receipt: Receipt,
  "shopping-cart": ShoppingCart,
  truck: Truck,
  user: User,
  users: Users,
};

export function kindIcon(name: string | undefined): Component | undefined {
  return name ? ICONS[name] : undefined;
}

export function kindLabel(kind: SearchKind, locale: string): string {
  return kind.label[locale] ?? kind.label.de ?? kind.label.en ?? kind.key;
}

/** Every keyword of a kind in every language, plus the account's own. */
export function kindKeywords(
  kind: SearchKind,
  settings: SearchSettings,
): string[] {
  const words = [
    ...Object.values(kind.keywords ?? {}).flat(),
    ...(settings.keywords[kind.key] ?? []),
  ].map((w) => w.toLowerCase());
  return [...new Set(words)];
}

// ── Catalogs ───────────────────────────────────────────────────────────────

const catalogs = new Map<string, Promise<SearchCatalog | null>>();
const RETRY_MS = 60_000;

/**
 * A module's catalog, fetched once per session. A module without the
 * universal search (404) or a failure yields null; a failure is retried after
 * a minute, a 404 not before the next reload.
 */
export function loadCatalog(
  module: string,
  request: UniversalSearchContext["request"] = moduleRequest,
): Promise<SearchCatalog | null> {
  const cached = catalogs.get(module);
  if (cached) return cached;
  const pending = (async () => {
    try {
      const res = await request(module, "/search/kinds");
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`catalog ${module}: ${res.status}`);
      const body = (await res.json()) as SearchCatalog;
      const catalog: SearchCatalog = {
        module,
        kinds: Array.isArray(body.kinds) ? body.kinds : [],
      };
      if (body.suggest === true) catalog.suggest = true;
      return catalog;
    } catch {
      setTimeout(() => catalogs.delete(module), RETRY_MS);
      return null;
    }
  })();
  catalogs.set(module, pending);
  return pending;
}

/** Test seam. */
export function resetCatalogsForTests(): void {
  catalogs.clear();
}

// ── Sources ────────────────────────────────────────────────────────────────

function hitToCommand(
  hit: SearchHit,
  kind: SearchKind | undefined,
  module: SearchModule,
  ctx: UniversalSearchContext,
  category: NxCommandCategory,
): NxCommand {
  const label = kind ? kindLabel(kind, ctx.locale) : hit.kind;
  const listHit = hit.match === "list";
  const title = listHit
    ? ctx.t("connect.search.listOf", { kind: label, name: hit.title })
    : hit.title;
  const hint = [
    hit.number && hit.number !== hit.title ? hit.number : "",
    hit.subtitle,
  ]
    .filter(Boolean)
    .join(" · ");
  const fields: { label: string; value: string }[] = [];
  if (hit.number)
    fields.push({ label: ctx.t("connect.search.number"), value: hit.number });
  if (hit.date) {
    fields.push({
      label: ctx.t("connect.search.date"),
      value: ctx.formatDate ? ctx.formatDate(hit.date) : hit.date.slice(0, 10),
    });
  }
  fields.push({
    label: ctx.t("connect.search.kind"),
    value: `${module.label} › ${label}`,
  });
  return {
    id: `${hit.kind}:${hit.id}`,
    label: title,
    hint: hint || undefined,
    snippet: hit.snippet || undefined,
    subgroup: label,
    icon: kindIcon(kind?.icon),
    match: hit.match,
    category,
    href: hit.target,
    transient: listHit,
    preview: {
      title,
      subtitle: hit.subtitle,
      fields,
      text: hit.snippet || undefined,
    },
    run: () => ctx.open(hit.target),
  };
}

/** How many hits a search narrowed to one kind asks for. */
export const SCOPED_LIMIT = 20;

/** The keyword that narrows the palette to a kind, in the UI language. */
function scopeKeyword(kind: SearchKind, locale: string): string | undefined {
  return (
    kind.keywords[locale]?.[0] ?? Object.values(kind.keywords ?? {}).flat()[0]
  );
}

/** Maps a module's answer to palette entries, kinds in the account's order. */
export function answerToCommands(
  answer: SearchAnswer,
  catalog: SearchCatalog,
  module: SearchModule,
  query: string,
  ctx: UniversalSearchContext,
  scoped = false,
): NxCommand[] {
  const category: NxCommandCategory =
    module.key === MASTER_MODULE ? "person" : "record";
  const kinds = new Map(catalog.kinds.map((k) => [k.key, k]));
  const results = orderKinds(
    ctx.settings,
    module.key,
    answer.results.map((r) => ({ ...r, key: r.kind })),
  );
  const out: NxCommand[] = [];
  for (const result of results) {
    const kind = kinds.get(result.kind);
    for (const hit of result.hits)
      out.push(hitToCommand(hit, kind, module, ctx, category));
    if (!result.more || !kind) continue;
    const label = kindLabel(kind, ctx.locale);
    const keyword = scopeKeyword(kind, ctx.locale);
    // Unscoped: narrow the palette to the kind with more hits. Scoped (already
    // all the palette shows): hand over to the kind's own list.
    if (!scoped && keyword && ctx.reopen) {
      const reopen = ctx.reopen;
      out.push({
        id: `more:${kind.key}`,
        label: ctx.t("connect.search.more", { kind: label }),
        subgroup: label,
        icon: kindIcon(kind.icon),
        category,
        href: kind.listTarget || undefined,
        transient: true,
        run: () => reopen(`${keyword} ${query}`),
      });
    } else if (kind.listTarget) {
      const target = kind.listTarget;
      out.push({
        id: `more:${kind.key}`,
        label: ctx.t("connect.search.openList", { kind: label }),
        subgroup: label,
        icon: kindIcon(kind.icon),
        category,
        href: target,
        transient: true,
        run: () => ctx.open(target),
      });
    }
  }
  return out;
}

/**
 * One palette source per module that has a catalog and is not switched off.
 * Sources come back in the account's module order (`order`).
 */
export async function buildUniversalSources(
  ctx: UniversalSearchContext,
): Promise<NxCommandSource[]> {
  const request = ctx.request ?? moduleRequest;
  const enabled = ctx.modules.filter(
    (m) => !ctx.settings.disabledModules.includes(m.key),
  );
  const catalogsLoaded = await Promise.all(
    enabled.map((m) => loadCatalog(m.key, request)),
  );
  const sources: NxCommandSource[] = [];
  enabled.forEach((module, i) => {
    const catalog = catalogsLoaded[i];
    if (!catalog) return;
    const kinds = orderKinds(ctx.settings, module.key, enabledCatalogKinds(ctx.settings, catalog.kinds));
    if (!kinds.length) return;
    const scopes: NxCommandScope[] = kinds.map((k) => ({
      id: k.key,
      label: kindLabel(k, ctx.locale),
      keywords: kindKeywords(k, ctx.settings),
      icon: kindIcon(k.icon),
    }));
    const fulltext = !ctx.settings.fulltextOff.includes(module.key);
    sources.push({
      id: `search:${module.key}`,
      group: module.label,
      order: moduleRank(ctx.settings, module.key),
      category: module.key === MASTER_MODULE ? "person" : "record",
      minQueryLength: 1,
      scopes,
      async search(query, signal, context) {
        const params = new URLSearchParams({
          q: query,
          limit: String(
            context?.scope
              ? Math.max(ctx.settings.limit, SCOPED_LIMIT)
              : ctx.settings.limit,
          ),
        });
        const narrowed = context?.scope
          ? [context.scope]
          : kinds.map((k) => k.key);
        params.set("kind", narrowed.join(","));
        if (!fulltext) params.set("fulltext", "false");
        const res = await request(module.key, `/search/hits?${params}`, signal);
        if (!res.ok) throw new Error(`search ${module.key}: ${res.status}`);
        const answer = (await res.json()) as SearchAnswer;
        return answerToCommands(
          answer,
          catalog,
          module,
          query,
          ctx,
          !!context?.scope,
        );
      },
    });
  });
  return sources;
}

/** Kinds the account has not switched off. Sources and suggestions share this. */
function enabledCatalogKinds<T extends { key: string }>(
  settings: SearchSettings,
  kinds: readonly T[],
): T[] {
  return kinds.filter((kind) => !settings.disabledKinds.includes(kind.key));
}

/** How many suggestions one module is asked for. The palette shows the best. */
const SUGGEST_LIMIT = 3;

function readSuggestionAnswer(module: string, body: unknown): SuggestionAnswer {
  const record = body as { module?: unknown; suggestions?: unknown } | null;
  const list = record?.suggestions;
  const suggestions: SuggestionWire[] = [];
  if (Array.isArray(list)) {
    for (const item of list) {
      if (!item || typeof item !== "object") continue;
      const row = item as SuggestionWire & { text?: unknown; score?: unknown };
      if (typeof row.text !== "string" || row.text === "") continue;
      const score = typeof row.score === "number" ? row.score : Number.NaN;
      if (!Number.isFinite(score)) continue;
      const suggestion: SuggestionWire = { text: row.text, score };
      if (typeof row.source === "string") suggestion.source = row.source;
      if (typeof row.tier === "number") suggestion.tier = row.tier;
      if (row.reason === "correction" || row.reason === "synonym") suggestion.reason = row.reason;
      if (typeof row.distance === "number") suggestion.distance = row.distance;
      suggestions.push(suggestion);
    }
  }
  const named = typeof record?.module === "string" ? record.module : module;
  return { module: named, suggestions };
}

/**
 * One palette suggester for every module whose catalog says `suggest: true`.
 * Disabled modules, modules with full text off, and modules whose kinds are
 * all disabled are not asked. A palette narrowed to a kind (`context.scope`)
 * asks only that kind's module, and a disabled kind asks nothing. A failed
 * module is skipped. `actions` is the shared dictionary: a typed word of the
 * user's own dictionary is not asked for a spelling, a blocked suggestion is
 * dropped. A direct mapping (`reason` set) is still asked for and kept. The
 * palette picks the highest score itself, so this suggester returns only the
 * merged winner. A synonym (`reason: "synonym"`) wins only while no word of
 * the whole query (`context.query`) has a correction — a spelling or a learned
 * one — and it is returned `confirmable: false`, since a synonym is no
 * spelling of the typed word. Looking at the other words costs one request
 * per module and word, and only when the winner is a synonym. `confirm` („„getippt“ ist richtig“) adds the typed word to
 * the dictionary. `accept` is the kit hook: „Übernehmen“ records the typed
 * word and the accepted spelling through `actions.learn`. `neverSuggest`
 * (the kit hook of K9) hides the suggested word through `actions.neverSuggest`.
 */
/** The palette suggester. `accept` is the kit hook of the same name. */
export interface UniversalSearchSuggester extends NxCommandSuggester {
  /** The user accepted `text` in place of the typed word `original`. */
  accept(original: string, text: string): void;
  /**
   * The user asked to stop suggesting `text`. Resolves once the word is hidden;
   * rejects when the write failed, after `ctx.writeReporter` showed it, so the
   * palette keeps its row and the user can try again.
   */
  neverSuggest(original: string, text: string): Promise<void>;
}

/** Words of the query besides the asked one that may be asked for a correction. */
const OTHER_WORDS_LIMIT = 6;

/** The words of `query` other than `term`, each once, that a suggestion can be asked for. */
function otherWords(query: string | undefined, term: string): string[] {
  if (!query) return [];
  const seen = new Set<string>([fold(term).text]);
  const out: string[] = [];
  for (const word of words(query)) {
    const text = query.slice(word.start, word.end);
    const key = fold(text).text;
    if (seen.has(key) || !isSuggestionEligible(text)) continue;
    seen.add(key);
    out.push(text);
    if (out.length >= OTHER_WORDS_LIMIT) break;
  }
  return out;
}

export function buildUniversalSuggester(
  ctx: UniversalSearchContext,
  actions: SearchDictionaryActions,
): UniversalSearchSuggester {
  const request = ctx.request ?? moduleRequest;
  /**
   * What the modules answered for the OTHER words of the query, so the
   * unchanged ones are not asked again on every keystroke of the last word.
   * Keyed by scope and folded word, pruned to the words of the latest query,
   * emptied whenever the dictionary changes. A failed or cancelled question is
   * never kept.
   */
  const otherAnswers = new Map<string, SuggestionAnswer[]>();
  const answerKey = (word: string, scope: string | undefined) =>
    `${scope ?? ""}\0${fold(word).text}`;
  /** Pairs (typed → suggested, folded) the last answer offered as a synonym. */
  const offeredSynonyms = new Set<string>();
  const pairKey = (original: string, text: string) =>
    `${fold(original).text}\0${fold(text).text}`;
  return {
    id: "search:suggest",
    async suggest(term, signal, context: NxCommandSuggestContext | undefined) {
      if (signal.aborted) return [];
      const enabled = ctx.modules.filter(
        (module) =>
          !ctx.settings.disabledModules.includes(module.key) &&
          !ctx.settings.fulltextOff.includes(module.key),
      );
      const catalogsLoaded = await Promise.all(
        enabled.map((module) => loadCatalog(module.key, request)),
      );
      if (signal.aborted) return [];
      const asking = enabled.filter((module, index) => {
        const catalog = catalogsLoaded[index];
        if (!catalog?.suggest) return false;
        const kinds = enabledCatalogKinds(ctx.settings, catalog.kinds);
        if (!kinds.length) return false;
        if (!context?.scope) return true;
        return kinds.some((kind) => kind.key === context.scope);
      });
      const filters = ctx.dictionaryFilters?.() ?? {
        hidden: new Set<string>(),
      };
      /** What the modules answered for one word; `complete` is false when a question failed. */
      const fetchGroups = async (word: string) => {
        let complete = true;
        const groups = await Promise.all(
          asking.map(async (module) => {
            try {
              const params = new URLSearchParams({
                q: word,
                match: "contains",
                caseSensitive: "false",
                limit: String(SUGGEST_LIMIT),
              });
              const res = await request(
                module.key,
                `/search/suggest?${params}`,
                signal,
              );
              if (!res.ok) {
                complete = false;
                return { module: module.key, suggestions: [] };
              }
              return readSuggestionAnswer(module.key, await res.json());
            } catch {
              complete = false;
              return { module: module.key, suggestions: [] };
            }
          }),
        );
        return { groups, complete: complete && !signal.aborted };
      };
      /**
       * The answers in one order. A word of the personal dictionary gets no
       * suggestion at all (`personal`, I-3024); a word only this tab knows
       * (just confirmed) keeps direct mappings only.
       */
      const rankGroups = (word: string, groups: SuggestionAnswer[]) => {
        const ranked = mergeSuggestions(groups, {
          hidden: filters.hidden,
          personal: filters.personal,
          queryTerm: word,
          isBlocked: (text) => actions.isBlocked(text),
          limit: SUGGEST_LIMIT,
        });
        // A known word suppresses spelling only. A direct mapping still wins.
        return actions.isOwnWord(word)
          ? ranked.filter((item) => item.reason !== undefined)
          : ranked;
      };
      const askWord = async (word: string) =>
        rankGroups(word, (await fetchGroups(word)).groups);
      /** The same, for another word of the query: reused while the word stays. */
      const askOtherWord = async (word: string) => {
        const key = answerKey(word, context?.scope);
        const known = otherAnswers.get(key);
        if (known) return rankGroups(word, known);
        const { groups, complete } = await fetchGroups(word);
        if (complete) otherAnswers.set(key, groups);
        return rankGroups(word, groups);
      };
      const visible = await askWord(term);
      if (signal.aborted) return [];
      let winner = visible[0];
      if (!winner) return [];
      let confirmable = true;
      if (winner.reason === "synonym") {
        // A synonym is the last resort: a correction of this word beats it,
        // and so does a correction of any other word of the query.
        const correction = visible.find((item) => item.reason !== "synonym");
        if (correction) {
          winner = correction;
        } else {
          const others = otherWords(context?.query, term);
          const keep = new Set(others.map((word) => answerKey(word, context?.scope)));
          for (const key of otherAnswers.keys()) if (!keep.has(key)) otherAnswers.delete(key);
          const answers = await Promise.all(others.map(askOtherWord));
          if (signal.aborted) return [];
          const corrected = answers.some((list) =>
            list.some((item) => item.reason !== "synonym"),
          );
          if (corrected) return [];
          // No spelling of the typed word: nothing to confirm it as.
          confirmable = false;
        }
      }
      const suggestion: NxCommandSuggestion = { text: winner.text, score: winner.score };
      if (winner.source) suggestion.source = winner.source;
      if (!confirmable) {
        suggestion.confirmable = false;
        // Clear first: the pair that is on screen must stay in the set.
        if (offeredSynonyms.size >= 100) offeredSynonyms.clear();
        offeredSynonyms.add(pairKey(term, winner.text));
      } else {
        offeredSynonyms.delete(pairKey(term, winner.text));
      }
      return [suggestion];
    },
    confirm(text) {
      otherAnswers.clear();
      void actions.addToDictionary(text);
    },
    accept(original, text) {
      otherAnswers.clear();
      // A synonym is no spelling of the typed word: taking it teaches no correction.
      if (offeredSynonyms.delete(pairKey(original, text))) return;
      void actions.learn(original, text);
    },
    async neverSuggest(_original, text) {
      try {
        await actions.neverSuggest(text);
      } catch (cause) {
        ctx.writeReporter?.failed(cause);
        throw cause;
      }
      otherAnswers.clear();
      ctx.writeReporter?.resolved?.();
    },
  };
}
